import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { trackById } from '../../../../utils/track-by';
import icPerson from '@iconify/icons-ic/twotone-person';
import icCameraAlt from '@iconify/icons-ic/twotone-camera-alt';
import icTune from '@iconify/icons-ic/twotone-tune';
import { PopoverRef } from '../../../../components/popover/popover-ref';
import { PopoverService } from '../../../../components/popover/popover.service';
import { ToolbarAgGridComponent } from '../../toolbar-ag-grid/toolbar-ag-grid.component';
import { UserProfileService, UserProfile } from '../../../../../app/helpers/services/user-profile.service';
import { Router } from '@angular/router';
import decode from 'jwt-decode';
import { ImpersonationService } from '../../../../../app/helpers/services/impersonation.service';

@Component({
    selector: 'vex-toolbar-user-dropdown',
    templateUrl: './toolbar-user-dropdown.component.html',
    styleUrls: ['./toolbar-user-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ToolbarUserDropdownComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  trackById = trackById;
  icPerson = icPerson;
  icCameraAlt = icCameraAlt;
  icTune = icTune;

  uploading = false;
  uploadError = '';
  isAdmin = false;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly popoverRef: PopoverRef<ToolbarUserDropdownComponent>,
    private readonly popover: PopoverService,
    readonly userProfileService: UserProfileService,
    private readonly router: Router,
    private readonly impersonationService: ImpersonationService,
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.getCurrentRole() === 'admin';
    // Profile is already loaded by ToolbarUserComponent; this is a no-op if so.
    this.userProfileService.loadProfile().then(() => this.cd.markForCheck());
  }

  /** Initials fallback when no photo is set */
  getInitials(profile: UserProfile | null): string {
    if (!profile) return '';
    const parts = profile.name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : (parts[0]?.[0] ?? '').toUpperCase();
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // Reset input so re-selecting the same file fires the event again
    input.value = '';

    this.uploading = true;
    this.uploadError = '';
    this.cd.markForCheck();

    try {
      await this.userProfileService.uploadPhoto(file);
    } catch {
      this.uploadError = 'Upload failed. Please try again.';
    } finally {
      this.uploading = false;
      this.cd.markForCheck();
    }
  }

  /** Open the grid-customize panel, anchored to the profile button. */
  openGridCustomize(): void {
    // The profile button element was passed through as popover data so we can
    // anchor to a stable element after closing this dropdown.
    const origin = this.popoverRef.data as unknown as HTMLElement;
    this.popoverRef.close();

    if (!origin) return;

    this.popover.open({
      content: ToolbarAgGridComponent,
      origin,
      offsetY: 12,
      position: [
        { originX: 'end',    originY: 'bottom', overlayX: 'end',    overlayY: 'top'    },
        { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom' },
      ],
    });
  }

  impersonate(): void {
    this.popoverRef.close();
    this.router.navigate(['/apps/admin/users']);
  }

  logout(): void {
    this.impersonationService.clearSession();
    this.popoverRef.close();
    window.location.replace('/login');
  }

  private getCurrentRole(): string {
    const token = localStorage.getItem('token');
    if (!token) return '';
    try {
      const payload = decode(token) as any;
      return payload?.role || '';
    } catch {
      return '';
    }
  }
}
